import React, { useMemo, useRef } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../context/AuthContext";
import { usePortfolio } from "../context/PortfolioContext";
import { fmtINR, fmtUnits, fmtPct, longDate } from "../lib/format";
import { ArrowDownRight, ArrowUpRight, Loader2, TrendingUp } from "lucide-react";
import { stampedName } from "../lib/csv";
import ExportButtons from "../components/ExportButtons";

// ----- XIRR via Newton's method ----- //
function xirr(cashflows, guess = 0.1) {
  // cashflows: [{ date: Date, amount: number }] - inflows positive, outflows negative
  if (!cashflows || cashflows.length < 2) return null;
  const sorted = [...cashflows].sort((a, b) => a.date - b.date);
  const t0 = sorted[0].date;
  const years = sorted.map((c) => (c.date - t0) / (365.25 * 24 * 3600 * 1000));
  const amounts = sorted.map((c) => c.amount);

  const hasPos = amounts.some((a) => a > 0);
  const hasNeg = amounts.some((a) => a < 0);
  if (!hasPos || !hasNeg) return null;

  let rate = guess;
  for (let i = 0; i < 100; i++) {
    let npv = 0, dnpv = 0;
    for (let k = 0; k < amounts.length; k++) {
      const denom = Math.pow(1 + rate, years[k]);
      npv += amounts[k] / denom;
      if (years[k] !== 0) dnpv -= (years[k] * amounts[k]) / (denom * (1 + rate));
    }
    if (Math.abs(npv) < 1e-6) return rate;
    if (dnpv === 0) return null;
    const next = rate - npv / dnpv;
    if (!isFinite(next)) return null;
    if (Math.abs(next - rate) < 1e-7) return next;
    rate = next;
    if (rate < -0.9999) rate = -0.9999; // prevent exploding
  }
  return rate;
}

function cagr(startValue, endValue, years) {
  if (!startValue || startValue <= 0 || !years || years <= 0) return null;
  return Math.pow(endValue / startValue, 1 / years) - 1;
}

export default function MyHoldings() {
  const { user } = useAuth();
  const { members, summary, transactions, loading } = usePortfolio();
  const printRef = useRef(null);

  const me = members.find((m) => m.id === user?.memberId);

  const myFlows = useMemo(() => {
    return transactions
      .filter((c) => c.memberId === user?.memberId)
      .slice()
      .sort((a, b) => {
        const da = new Date(a.date);
        const db = new Date(b.date);
        if (db - da !== 0) return db - da;
        return (b.id || "").localeCompare(a.id || "");
      });
  }, [transactions, user]);

  // Build cashflow series for XIRR + CAGR
  const metrics = useMemo(() => {
    if (!me || !user?.memberId) return {};
    const txs = transactions.filter((c) => c.memberId === user.memberId);
    if (txs.length === 0) return {};

    // Asc sort
    const asc = [...txs].sort((a, b) => new Date(a.date) - new Date(b.date));
    const firstDate = new Date(asc[0].date);
    const now = new Date();
    const years = (now - firstDate) / (365.25 * 24 * 3600 * 1000);

    // XIRR: outflows (subscriptions) = negative; inflows (redemptions) = positive;
    // Bonus is excluded — no real cashflow.
    // Final positive inflow = current overall value (capital + bonus appreciation).
    const flows = asc
      .filter((t) => t.type !== "bonus")
      .map((t) => {
        const cr = t.fundsCr || 0;
        const dr = t.fundsDr || 0;
        return { date: new Date(t.date), amount: cr > 0 ? -cr : dr };
      });
    const finalValue = (me.overallCurrentValue != null ? me.overallCurrentValue : me.currentValue) || 0;
    flows.push({ date: now, amount: finalValue });

    const xirrRate = xirr(flows);

    // CAGR: capital invested vs overall current value (capital appreciation + bonus)
    const cagrRate = cagr(me.capitalInvested || 0, finalValue, years);

    return {
      xirr: xirrRate !== null ? xirrRate * 100 : null,
      cagr: cagrRate !== null ? cagrRate * 100 : null,
      years: years > 0 ? years.toFixed(2) : "0",
      firstDate: asc[0].date,
    };
  }, [transactions, user, me]);

  const hasBonus = !!me && ((me.bonusUnits || 0) > 0 || (me.bonusCapital || 0) > 0);

  // Build CSV + PDF payloads for the export buttons
  const exportPayload = useMemo(() => {
    if (!me) return null;
    const csvHeaders = ["Date", "Type", "NAV", "Units", "Amount", "Details"];
    const csvRows = myFlows.map((f) => {
      const isB = f.type === "bonus";
      const inflow = !isB && (f.fundsCr || 0) > 0;
      const amount = isB ? f.fundsCr : (inflow ? f.fundsCr : -f.fundsDr);
      const units = (f.unitsCr || 0) > 0 ? f.unitsCr : f.unitsDr;
      return [
        f.date,
        isB ? "Bonus" : (inflow ? "Subscription" : "Redemption"),
        f.nav, units, amount, f.details || "",
      ];
    });
    csvRows.push([]);
    csvRows.push(["Capital Invested", "", "", "", me.capitalInvested, ""]);
    csvRows.push(["Capital Units", "", "", me.units, "", ""]);
    csvRows.push(["Avg Cost NAV", "", me.avgNAV, "", "", ""]);
    csvRows.push(["Capital Current Value", "", "", "", me.currentValue, ""]);
    csvRows.push(["Capital Gain/Loss", "", "", "", me.gainLoss, `${(me.gainLossPct || 0).toFixed(2)}%`]);
    if (hasBonus) {
      csvRows.push([]);
      csvRows.push(["Bonus Capital", "", "", "", me.bonusCapital, ""]);
      csvRows.push(["Bonus Units", "", "", me.bonusUnits, "", ""]);
      csvRows.push(["Bonus Value", "", "", "", me.bonusValue, ""]);
      csvRows.push(["Bonus Gain", "", "", "", me.bonusGainLoss, `${(me.bonusGainPct || 0).toFixed(2)}%`]);
      csvRows.push([]);
      csvRows.push(["Total Units", "", "", me.overallUnits, "", ""]);
      csvRows.push(["Overall Current Value", "", "", "", me.overallCurrentValue, ""]);
      csvRows.push(["Overall Gain/Loss", "", "", "", me.overallGainLoss, `${(me.overallGainPct || 0).toFixed(2)}%`]);
    }

    const pdfColumns = ["Date", "Type", "NAV", "Units", "Amount"];
    const pdfRows = myFlows.map((f) => {
      const isB = f.type === "bonus";
      const inflow = !isB && (f.fundsCr || 0) > 0;
      const amount = isB ? f.fundsCr : (inflow ? f.fundsCr : -f.fundsDr);
      const units = (f.unitsCr || 0) > 0 ? f.unitsCr : f.unitsDr;
      return [
        f.date,
        isB ? "Bonus" : (inflow ? "Subscription" : "Redemption"),
        "₹" + Number(f.nav || 0).toFixed(4),
        fmtUnits(units),
        fmtINR(amount),
      ];
    });
    const pdfSummary = [
      ["Capital invested", fmtINR(me.capitalInvested || 0)],
      ["Capital units", fmtUnits(me.units || 0)],
      ["Avg cost NAV", "₹" + (me.avgNAV || 0).toFixed(4)],
      ["Current value", fmtINR(me.currentValue || 0)],
      ["Gain/Loss", `${fmtINR(me.gainLoss || 0)} (${(me.gainLossPct || 0).toFixed(2)}%)`],
    ];
    if (hasBonus) {
      pdfSummary.push(["Bonus units", fmtUnits(me.bonusUnits || 0)]);
      pdfSummary.push(["Bonus value", fmtINR(me.bonusValue || 0)]);
      pdfSummary.push(["Overall value", fmtINR(me.overallCurrentValue || me.currentValue || 0)]);
    }
    return { csvHeaders, csvRows, pdfColumns, pdfRows, pdfSummary };
  }, [myFlows, me, hasBonus]);

  if (loading)
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );

  if (!me) {
    return (
      <AppShell>
        <div className="text-slate-400">No holdings linked to this account.</div>
      </AppShell>
    );
  }

  const positive = me.gainLoss >= 0;
  const overallPositive = (me.overallGainLoss || 0) >= 0;
  const bonusPositive = (me.bonusGainLoss || 0) >= 0;

  return (
    <AppShell>
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          aside, header, nav[class*="fixed"] { display: none !important; }
          main { padding: 0 !important; }
          .print-root { background: white !important; color: black !important; }
          .print-root * { color: black !important; border-color: #ddd !important; background-color: white !important; }
          .print-card { border: 1px solid #ccc !important; page-break-inside: avoid; }
        }
      `}</style>

      <div ref={printRef} className="print-root">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
              <span className="pulse-dot" style={{ width: 6, height: 6 }} />
              My Holdings
            </div>
            <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">{me.name}</h1>
            <div className="text-sm text-slate-400">
              {fmtPct(me.holdingPct)} of the pool ·{" "}
              {(me.overallUnits || me.units) > 0 ? `${fmtUnits(me.overallUnits || me.units)} total units held` : "No units held"}
              {hasBonus && me.bonusUnits > 0 && (
                <span className="text-amber-300"> · incl. {fmtUnits(me.bonusUnits)} bonus</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 no-print flex-wrap">
            <ExportButtons
              testIdPrefix="my-holdings-export"
              csv={{
                filename: stampedName(`my_holdings_${(me.name || "").replace(/\s+/g, "_")}`),
                headers: exportPayload.csvHeaders,
                rows: exportPayload.csvRows,
              }}
              pdf={{
                title: `${me.name} — Holdings statement`,
                subtitle: `As of ${longDate(new Date().toISOString())}`,
                filename: `my_holdings_${(me.name || "").replace(/\s+/g, "_")}`,
                columns: exportPayload.pdfColumns,
                rows: exportPayload.pdfRows,
                summary: exportPayload.pdfSummary,
              }}
            />
          </div>
        </div>

        <div className="mt-6 holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-5 md:p-6 print-card" data-testid="capital-current-value-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Capital Holdings</div>
              <div className="text-xs text-slate-400 mt-1">Current Value (from Subscriptions)</div>
              <div className="mt-1 text-3xl md:text-4xl font-semibold text-white">
                {fmtINR(me.currentValue)}
              </div>
            </div>
            <div
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${
                positive
                  ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                  : "text-rose-400 bg-rose-500/10 border-rose-500/30"
              }`}
            >
              {positive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {fmtPct(me.gainLossPct)}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-slate-800 pt-5">
            <KV label="Capital Invested" value={fmtINR(me.capitalInvested)} />
            <KV label="Units Held" value={fmtUnits(me.units)} />
            <KV label="Avg. Cost NAV" value={`₹${me.avgNAV.toFixed(4)}`} />
            <KV label="Current NAV" value={`₹${(summary?.currentNAV || 0).toFixed(4)}`} />
            <KV label="Gain / Loss" value={fmtINR(me.gainLoss)} positive={positive} />
            <KV label="Holding %" value={fmtPct(me.holdingPct)} />
            <KV label="Absolute Return" value={fmtPct(me.gainLossPct)} positive={positive} />
            <KV label="Investment Duration" value={metrics.years ? `${metrics.years} yrs` : "—"} />
          </div>
        </div>

        {/* Bonus card — shown only when member has bonus holdings */}
        {hasBonus && (
          <div className="mt-6 bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/40 rounded-xl p-5 md:p-6 print-card" data-testid="bonus-current-value-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] text-amber-300 uppercase tracking-widest flex items-center gap-2">
                  <span className="text-base">★</span> Bonus Holdings
                </div>
                <div className="text-xs text-slate-400 mt-1">Current Value (from Bonus units)</div>
                <div className="mt-1 text-3xl md:text-4xl font-semibold text-white">
                  {fmtINR(me.bonusValue)}
                </div>
              </div>
              <div
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${
                  bonusPositive
                    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                    : "text-rose-400 bg-rose-500/10 border-rose-500/30"
                }`}
              >
                {bonusPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {fmtPct(me.bonusGainPct)}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-amber-500/20 pt-5">
              <KV label="Bonus Capital" value={fmtINR(me.bonusCapital)} />
              <KV label="Bonus Units" value={fmtUnits(me.bonusUnits)} />
              <KV label="Bonus NAV" value={me.bonusUnits > 0 ? `₹${(me.bonusCapital / me.bonusUnits).toFixed(4)}` : "—"} />
              <KV label="Current NAV" value={`₹${(summary?.currentNAV || 0).toFixed(4)}`} />
              <KV label="Bonus Value" value={fmtINR(me.bonusValue)} />
              <KV label="Bonus Gain" value={fmtINR(me.bonusGainLoss)} positive={bonusPositive} />
              <KV label="Bonus Gain %" value={fmtPct(me.bonusGainPct)} positive={bonusPositive} />
              <KV label="Type" value="Free Units" />
            </div>
          </div>
        )}

        {/* Overall combined card — only shown when bonus exists */}
        {hasBonus && (
          <div className="mt-6 holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm border-2 border-emerald-500/40 rounded-xl p-5 md:p-6 print-card" data-testid="overall-current-value-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] text-emerald-300 uppercase tracking-widest">Overall (Capital + Bonus)</div>
                <div className="text-xs text-slate-400 mt-1">Total Current Value</div>
                <div className="mt-1 text-3xl md:text-4xl font-semibold text-white">
                  {fmtINR(me.overallCurrentValue)}
                </div>
              </div>
              <div
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${
                  overallPositive
                    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                    : "text-rose-400 bg-rose-500/10 border-rose-500/30"
                }`}
              >
                {overallPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {fmtPct(me.overallGainPct)}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-slate-800 pt-5">
              <KV label="Total Units Held" value={fmtUnits(me.overallUnits)} />
              <KV label="Capital Value" value={fmtINR(me.currentValue)} />
              <KV label="Bonus Value" value={fmtINR(me.bonusValue)} />
              <KV label="Capital Invested" value={fmtINR(me.capitalInvested)} />
              <KV label="Overall Gain / Loss" value={fmtINR(me.overallGainLoss)} positive={overallPositive} />
              <KV label="Overall % Gain / Loss" value={fmtPct(me.overallGainPct)} positive={overallPositive} />
              <KV label="Holding %" value={fmtPct(me.holdingPct)} />
              <KV label="Current NAV" value={`₹${(summary?.currentNAV || 0).toFixed(4)}`} />
            </div>
            <div className="mt-3 text-[10px] text-slate-500">
              Overall % computed against Capital Invested (bonus capital not paid in).
            </div>
          </div>
        )}

        {/* Annualised returns card */}
        {(metrics.cagr !== undefined || metrics.xirr !== undefined) && (
          <div className="mt-6 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-xl p-5 print-card">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-emerald-400" />
              <div className="text-sm font-semibold text-white">Annualised Returns</div>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Since first investment ({metrics.firstDate ? longDate(metrics.firstDate) : "—"})
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">XIRR</div>
                <div className={`mt-1 text-2xl md:text-3xl font-semibold ${
                  metrics.xirr == null ? "text-slate-500" :
                  metrics.xirr >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {metrics.xirr == null ? "—" : fmtPct(metrics.xirr)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Money-weighted return (considers timing of each cashflow)
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">CAGR</div>
                <div className={`mt-1 text-2xl md:text-3xl font-semibold ${
                  metrics.cagr == null ? "text-slate-500" :
                  metrics.cagr >= 0 ? "text-emerald-400" : "text-rose-400"
                }`}>
                  {metrics.cagr == null ? "—" : fmtPct(metrics.cagr)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Compounded annual growth rate (capital base vs current value)
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-4 md:p-5 print-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-white">Your Transactions</div>
              <div className="text-xs text-slate-500">Subscriptions &amp; redemptions</div>
            </div>
            <div className="text-xs text-slate-500">{myFlows.length} records</div>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] text-slate-400 uppercase tracking-wider">
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-right py-2">NAV</th>
                  <th className="text-right py-2">Units</th>
                  <th className="text-right py-2">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {myFlows.length === 0 && (
                  <tr><td colSpan={5} className="text-sm text-slate-500 py-6 text-center">No transactions yet.</td></tr>
                )}
                {myFlows.map((f) => {
                  const isBonus = f.type === "bonus";
                  const inflow = !isBonus && (f.fundsCr || 0) > 0;
                  const amount = isBonus ? f.fundsCr : (inflow ? f.fundsCr : -f.fundsDr);
                  const units = (f.unitsCr || 0) > 0 ? f.unitsCr : f.unitsDr;
                  const amtColor = isBonus ? "text-amber-400" : (inflow ? "text-emerald-400" : "text-rose-400");
                  const sign = isBonus ? "★ " : (inflow ? "+" : "");
                  return (
                    <tr key={f.id}>
                      <td className="py-3 text-slate-300 whitespace-nowrap">{longDate(f.date)}</td>
                      <td className="py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded border ${
                          isBonus
                            ? "bg-amber-500/10 text-amber-300 border-amber-500/40"
                            : inflow
                              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                              : "bg-rose-500/10 text-rose-300 border-rose-500/30"
                        }`}>
                          {isBonus ? "Bonus" : (inflow ? "Subscription" : "Redemption")}
                        </span>
                      </td>
                      <td className="py-3 text-right text-slate-400 font-mono text-xs">₹{f.nav.toFixed(4)}</td>
                      <td className="py-3 text-right text-slate-400 font-mono text-xs">{fmtUnits(units)}</td>
                      <td className={`py-3 text-right font-semibold ${amtColor}`}>
                        {sign}{fmtINR(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function KV({ label, value, positive }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
      <div
        className={`mt-1 font-semibold ${
          positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
